import {Component, Injector, OnDestroy} from '@angular/core';
import {FrontendFormComponent} from '@abstract/frontend.form.component';
import {TechnicalDeviceNamespace} from '@namespaces/technical-device.namespace';
import {WorkingObjectNamespace} from '@namespaces/working-object.namespace';
import {ActivatedRoute, Router} from '@angular/router';
import {WorkingObjectsApiService} from '@api/working-objects.api.service';
import {SelectValueInterface} from '@interfaces/select-value.interface';
import {AuthService} from '@services/auth.service';
import {UserNamespace} from '@namespaces/user.namespace';
import {TechnicalDeviceApiService} from '@api/technical-device.api.service';
import {Subject} from 'rxjs';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {RoutesStorage} from '@helpers/routes.helper';
import {NotyMessagesEnum} from '@helpers/noty-messages.enum';

@Component({
    selector: 'app-technical-device-form',
    templateUrl: 'technical-device-form.component.html'
})

export class TechnicalDeviceFormComponent extends FrontendFormComponent implements OnDestroy {

    public afterSaveMessage = NotyMessagesEnum.technicalDeviceUpdateMessage;

    public statuses = TechnicalDeviceNamespace.StatusesList;

    /**
     * Инфа по объекту, для которого создается ТУ
     */
    public workingObject: WorkingObjectNamespace.ListItem;

    /**
     * Список объектов, доступных пользователю для выбора в селекте формы
     */
    public objects: SelectValueInterface[] = [];

    public deviceUrl: string;
    public objectUrl: string;

    public canSetStatus = false;

    public types: TechnicalDeviceNamespace.TechnicalDeviceTypeInterface[] = [];

    private destroyed$ = new Subject();

    constructor(
        injector: Injector,
        public formService: WorkingObjectsApiService,
        private route: ActivatedRoute,
        private router: Router,
        private authService: AuthService,
        private deviceApi: TechnicalDeviceApiService,
    ) {
        super(injector);
    }

    init() {
        this.form = this._fb.group({
            title: [''],
            status: [TechnicalDeviceNamespace.StatusesEnum.new],
            location: [''],
            object_id: [''],
            type_id: ['']
        });

        const device: TechnicalDeviceNamespace.ApiDataInterface = this.route.snapshot.data.device;

        this.form.get('object_id').valueChanges.pipe(
            distinctUntilChanged(),
            filter((v) => Boolean(v)),
            takeUntil(this.destroyed$)
        ).subscribe((objectId) => {
            this.updateTypesList(+objectId);
        });

        if (device) {
            this.deviceUrl = RoutesStorage.TechnicalDevice.view(device.id);
            this.workingObject = device.working_object;
            this.setFormValues(device, this.form);
            this.form.get('object_id').disable();
            this.form.get('type_id').disable();
        } else {
            this.workingObject = this.route.snapshot.data.object;
            this.statuses = this.statuses.filter(s => s.id === TechnicalDeviceNamespace.StatusesEnum.new);
            this.form.get('status').disable();
        }

        this.objectUrl = RoutesStorage.WorkingObject.view(this.workingObject.id);

        /**
         * Получаем список доступных объектов и выбираем объект, если он единственный
         */
        this.objects = this.route.snapshot.data.objects || [];

        if (this.objects && this.objects.length === 1) {
            this.form.get('object_id').patchValue(this.objects[0].id);
        } else {
            /**
             * Обновляем поле, чтобы стриггерить его изменение и запросить типы
             */
            this.form.get('object_id').updateValueAndValidity();
        }

        this.canSetStatus = this.authService.hasPermissions([UserNamespace.Permission.technicalDeviceSetStatus]);
    }

    preventSave() {
        return !this.workingObject;
    }

    get saveUrl() {
        return this.formService.getTechnicalDevicesPath(this.workingObject.id);
    }

    afterSave(device: TechnicalDeviceNamespace.ApiDataInterface) {
        super.afterSave();
        const id = device.id;
        this.router.navigate([RoutesStorage.TechnicalDevice.view(id)]);
    }

    private updateTypesList(objectId: number) {
        this.deviceApi.get({
            url: this.deviceApi.getTechnicalDeviceTypesPath(objectId),
        }).pipe(
            takeUntil(this.destroyed$)
        ).subscribe((data: TechnicalDeviceNamespace.TechnicalDeviceTypeInterface[]) => {
            this.types = data || [];
        }, () => {
            this.types = [];
        });
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }
}
